import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BottleneckAnalysisPanel = ({ bottlenecks, onBulkIntervention }) => {
  const getMilestoneLabel = (type) => {
    const labels = {
      organization_profile: 'Organization Profile',
      employee_import: 'Employee Import',
      compliance_setup: 'Compliance Setup',
      team_access: 'Team Access'
    };
    return labels?.[type] || type;
  };

  const getSeverityStyles = (severity) => {
    const styles = {
      critical: {
        bg: 'bg-error/10',
        text: 'text-error',
        border: 'border-error/20',
        icon: 'AlertOctagon'
      },
      high: {
        bg: 'bg-warning/10',
        text: 'text-warning',
        border: 'border-warning/20',
        icon: 'AlertTriangle'
      },
      medium: {
        bg: 'bg-primary/10',
        text: 'text-primary',
        border: 'border-primary/20',
        icon: 'Info'
      }
    };
    return styles?.[severity] || styles?.medium;
  };

  const getRecommendedAction = (milestoneType) => {
    const actions = {
      organization_profile: 'Send setup reminder email with step-by-step guide',
      employee_import: 'Offer bulk import template and support call',
      compliance_setup: 'Schedule compliance configuration walkthrough',
      team_access: 'Provide role assignment tutorial and best practices'
    };
    return actions?.[milestoneType] || 'Contact client for assistance';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Bottleneck Analysis</h2>
        <Icon name="TrendingDown" size={20} className="text-error" />
      </div>

      {bottlenecks?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-4" />
          <p className="text-muted-foreground">No bottlenecks detected</p>
          <p className="text-xs text-muted-foreground mt-2">All clients are progressing smoothly</p>
        </div>
      ) : (
        <div className="space-y-4">
          {bottlenecks?.map((bottleneck, index) => {
            const styles = getSeverityStyles(bottleneck?.severity);
            return (
              <div
                key={index}
                className={`border ${styles?.border} ${styles?.bg} rounded-lg p-4`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <Icon name={styles?.icon} size={20} className={styles?.text} />
                  <div className="flex-1">
                    <h3 className={`font-semibold ${styles?.text} mb-1`}>
                      {getMilestoneLabel(bottleneck?.milestoneType)}
                    </h3>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
                      <span>{bottleneck?.affectedClients} clients affected</span>
                      <span>Avg: {bottleneck?.avgTimeStuck} days stuck</span>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${styles?.bg} ${styles?.text} border ${styles?.border}`}>
                    {bottleneck?.severity?.toUpperCase()}
                  </span>
                </div>

                <div className="bg-card/50 rounded p-3 mb-3">
                  <p className="text-xs font-medium text-foreground mb-1">Recommended Action:</p>
                  <p className="text-xs text-muted-foreground">
                    {getRecommendedAction(bottleneck?.milestoneType)}
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 text-xs"
                    onClick={() => alert(`Sending intervention for ${getMilestoneLabel(bottleneck?.milestoneType)}`)}
                  >
                    <Icon name="Send" size={14} className="mr-1" />
                    Send Intervention
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs"
                    onClick={() => alert(`Viewing details for ${getMilestoneLabel(bottleneck?.milestoneType)}`)}
                  >
                    <Icon name="Eye" size={14} />
                  </Button>
                </div>
              </div>
            );
          })}

          {bottlenecks?.length > 1 && (
            <Button
              onClick={onBulkIntervention}
              className="w-full mt-4"
              variant="primary"
            >
              <Icon name="Zap" size={16} className="mr-2" />
              Bulk Intervention for All Bottlenecks
            </Button>
          )}
        </div>
      )}

      {/* Common Friction Points */}
      <div className="mt-6 pt-6 border-t border-border">
        <h3 className="text-sm font-semibold text-foreground mb-3">Common Friction Points</h3>
        <div className="space-y-2">
          <div className="flex items-start gap-2 text-xs">
            <Icon name="Circle" size={8} className="text-muted-foreground mt-1" />
            <p className="text-muted-foreground">Incomplete organization profiles (missing tax ID or registration)</p>
          </div>
          <div className="flex items-start gap-2 text-xs">
            <Icon name="Circle" size={8} className="text-muted-foreground mt-1" />
            <p className="text-muted-foreground">Failed employee data imports (format issues)</p>
          </div>
          <div className="flex items-start gap-2 text-xs">
            <Icon name="Circle" size={8} className="text-muted-foreground mt-1" />
            <p className="text-muted-foreground">Compliance configuration errors (NASFUND/SWT setup)</p>
          </div>
          <div className="flex items-start gap-2 text-xs">
            <Icon name="Circle" size={8} className="text-muted-foreground mt-1" />
            <p className="text-muted-foreground">Team access delays (role assignment confusion)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BottleneckAnalysisPanel;